import React from 'react'
import { Palette, Box, Accessibility, Lightbulb, GraduationCap, ArrowUpRight } from 'lucide-react'
import Links from './Link'
import Reveal from './Reveal'
import Atmosphere from './Atmosphere'

const Prestation = () => {
  // Après navigation vers /prestations, défile vers la section concernée.
  const scrollToSection = (id) => {
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
    }, 500)
  }

  const services = [
    {
      title: 'Accessibilité & FALC',
      icon: Accessibility,
      path: 'accessibilité',
      items: ['Audit d’accessibilité numérique', 'Mise en accessibilité universelle', 'Traduction en FALC'],
    },
    {
      title: 'Création multimédia',
      icon: Palette,
      path: 'multimedia',
      items: ['Outils publicitaires 2D/3D', 'Jeux vidéo', 'Formations vidéoludiques', 'Communication accessible'],
    },
    {
      title: 'Modélisation 3D',
      icon: Box,
      path: 'modelisation',
      items: ["Images de synthèse", "Films d'animation", 'Environnements augmentés', 'Fichiers d’usinage CNC'],
    },
    {
      title: 'Sensibilisation handicap',
      icon: Lightbulb,
      path: 'sensibilisation',
      items: ['Ateliers artistiques', 'Ateliers numériques', 'Prestations sur-mesure'],
    },
    {
      title: 'Formations',
      icon: GraduationCap,
      path: 'formation',
      items: ['Accueil des publics en situation de handicap', 'Mise en accessibilité des supports'],
    },
  ]

  const audiences = [
    { img: '/entreprises.png', title: 'Entreprises & collectivités' },
    { img: '/studiodejeux.png', title: 'Studios de jeux vidéo' },
    { img: '/formation.png', title: 'Organismes de formation' },
    { img: '/decors3.png', title: 'Industries créatives & techniques' },
  ]

  return (
    <section className="relative overflow-hidden py-24 lg:py-32" id="Prestation">
      <Atmosphere variant="default" />

      <div className="container-pdv relative z-10">
        {/* En-tête */}
        <Reveal className="max-w-2xl">
          <span className="eyebrow">Nos prestations</span>
          <h2 className="mt-5 font-display font-extrabold leading-[1.05] text-[clamp(2rem,4.4vw,3.4rem)]">
            Un studio, <span className="text-gradient">cinq savoir-faire</span>
          </h2>
          <p className="mt-5 text-lg text-[#b6a9d8]">
            De l'idée au rendu final, nous concevons des contenus créatifs et accessibles à tous les publics.
          </p>
        </Reveal>

        {/* Cartes services */}
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon
            return (
              <Reveal key={s.path} delay={i * 80}>
                <Links
                  to="/prestations"
                  onClick={() => scrollToSection(s.path)}
                  className="group relative h-full card-surface ring-gradient rounded-3xl p-7 flex flex-col overflow-hidden transition-transform duration-500 hover:-translate-y-1.5 cursor-pointer"
                >
                  <span className="pointer-events-none absolute -top-4 right-4 font-display font-extrabold text-7xl text-white/[0.04] select-none">
                    {String(i + 1).padStart(2, '0')}
                  </span>

                  <span className="grid place-items-center h-14 w-14 rounded-2xl bg-grad-violet shadow-glow transition-transform duration-500 group-hover:scale-110">
                    <Icon size={26} className="text-white" />
                  </span>

                  <h3 className="mt-6 font-display font-bold text-2xl text-white">{s.title}</h3>

                  <ul className="mt-4 space-y-1.5 flex-1">
                    {s.items.map((it) => (
                      <li key={it} className="flex gap-2.5 text-[#b6a9d8]">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-violet-bright shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>

                  <span className="mt-6 inline-flex items-center gap-1.5 font-semibold text-violet-bright transition-colors group-hover:text-white">
                    En savoir plus
                    <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Links>
              </Reveal>
            )
          })}

          {/* Carte CTA finale */}
          <Reveal delay={services.length * 80}>
            <Links
              to="/prestations"
              className="group relative h-full rounded-3xl p-7 flex flex-col justify-between overflow-hidden bg-grad-violet shadow-glow transition-transform duration-500 hover:-translate-y-1.5"
            >
              <div
                className="absolute inset-0 opacity-30"
                style={{ background: 'radial-gradient(60% 80% at 80% 20%, rgba(255,255,255,0.5), transparent 60%)' }}
              />
              <h3 className="relative font-display font-extrabold text-2xl leading-tight text-white">
                Toutes nos prestations en détail
              </h3>
              <span className="relative mt-6 inline-flex items-center gap-2 font-semibold text-white">
                Explorer la page
                <ArrowUpRight size={20} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </Links>
          </Reveal>
        </div>

        {/* Pour qui ? */}
        <Reveal className="mt-24 max-w-2xl">
          <span className="eyebrow">Pour qui&nbsp;?</span>
          <h2 className="mt-5 font-display font-extrabold leading-[1.05] text-[clamp(1.8rem,3.8vw,3rem)]">
            Des publics <span className="text-gradient">exigeants</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {audiences.map((a, i) => (
            <Reveal key={a.title} delay={i * 90}>
              <div className="group relative overflow-hidden rounded-3xl card-surface ring-gradient aspect-[4/5]">
                <img
                  src={a.img}
                  alt={a.title}
                  className="h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-110 group-hover:opacity-100"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0218] via-[#0a0218]/40 to-transparent" />
                <h3 className="absolute bottom-5 left-5 right-5 font-display font-bold text-lg sm:text-xl text-white">
                  {a.title}
                </h3>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Prestation
